import { AuditedEntityDto, PagedAndSortedResultRequestDto } from '@abp/ng.core';
import { ReasoningEffort } from 'src/app/integrations/proxy/integration.model';
import { FlowType } from 'src/app/shared/designer/proxy/designer.model';

export interface AiAgentSummaryDto extends AuditedEntityDto<string> {
  name: string;
  description: string;
  avatar: string;
  lastPreparationDate?: Date;
  preparationState: PreparationState;
  isSupervisor: boolean;
  guardrailId?: string;
  hasExternalTools: boolean;
  hasExternalToolsInSubAgents: boolean;
  versionGroupId: string;
}

export interface GetAiAgentsInput extends PagedAndSortedResultRequestDto {
  filterText?: string;
}

export enum CollaborationMode {
  none,
  supervisor,
}

export enum CreativityLevel {
  precise,
  balanced,
  creative,
}

export enum Persona {
  friendly,
  formal,
  empathetic,
  excited,
}

export enum ResponseDetailLevel {
  concise,
  balanced,
  detailed,
}

export enum PreparationState {
  notPrepared,
  prepared,
  preparationFailed,
  rePreparationRequired,
}

export interface AiAgentCreateDto {
  name: string;
  description: string;
  instructions: string;
  avatarImage: File;
  collaborationMode: CollaborationMode;
  avoidHarmfulOrUnethicalContent: boolean;
  stayGroundedToVerifiedInformation: boolean;
  detectAndBlockJailbreakAttempts: boolean;
  maintainOutputConsistency: boolean;
  persona: Persona;
  responseDetailLevel: ResponseDetailLevel;
  creativityLevel: CreativityLevel;
  llmModel: string;
  isCustomLlmModelSelected: boolean;
  llmConfigurationId: string;
  projectId: string;
  projectVersionId: string;
  guardrailId?: string;
  reasoningEffort?: ReasoningEffort;
}

export interface AiAgentUpdateDto {
  name: string;
  description: string;
  instructions: string;
  isAvatarImageChanged: boolean;
  avatarImage: File;
  collaborationMode: CollaborationMode;
  avoidHarmfulOrUnethicalContent: boolean;
  stayGroundedToVerifiedInformation: boolean;
  detectAndBlockJailbreakAttempts: boolean;
  maintainOutputConsistency: boolean;
  persona: Persona;
  responseDetailLevel: ResponseDetailLevel;
  creativityLevel: CreativityLevel;
  llmModel?: string;
  isCustomLlmModelSelected: boolean;
  llmConfigurationId: string;
  projectId: string;
  projectVersionId: string;
  guardrailId?: string;
  reasoningEffort?: ReasoningEffort;
}

export interface AiAgentDto extends AuditedEntityDto<string> {
  name: string;
  description: string;
  instructions: string;
  avatar: string;
  collaborationMode: CollaborationMode;
  avoidHarmfulOrUnethicalContent: boolean;
  stayGroundedToVerifiedInformation: boolean;
  detectAndBlockJailbreakAttempts: boolean;
  maintainOutputConsistency: boolean;
  persona: Persona;
  responseDetailLevel: ResponseDetailLevel;
  creativityLevel: CreativityLevel;
  llmModel: string;
  isCustomLlmModelSelected: boolean;
  preparationState: PreparationState;
  lastPreparationDate?: Date;
  llmConfigurationId: string;
  projectVersionId: string;
  isSupervisor: boolean;
  guardrailId?: string;
  reasoningEffort?: ReasoningEffort;
}

export interface AiAgentDetailsDto {
  aiAgent: AiAgentDto;
  aiAgentSubAgents: AiAgentSubAgentDto[];
  aiAgentTools: AiAgentToolDto[];
  availableAiAgentSubAgents: AvailableAiAgentDto[];
}

export interface AiAgentSubAgentDto extends AuditedEntityDto<string> {
  supervisorAgentId: string;
  subAgentId: string;
  subAgentName: string;
  collaborationInstructions: string;
  status: ActivationStatus;
  avatar: string;
  preparationState: PreparationState;
  lastPreparationDate?: Date;
}

export interface AiAgentSubAgentCreateDto {
  subAgentId: string;
  collaborationInstructions: string;
}

export interface AiAgentSubAgentUpdateDto {
  collaborationInstructions: string;
}

export enum ActivationStatus {
  active,
  passive,
}

export enum AiAgentDetailNavigationItemType {
  aiAgentDetails,
  subAgent,
  tool,
}

export interface AiAgentAvatarInfo {
  aiAgentName: string;
  avatar: string;
}

export interface AiAgentToolDto extends AuditedEntityDto<string> {
  name: string;
  description: string;
  type: AiAgentToolType;
  aiAgentId: string;
  flowId: string;
  aiAgentToolParameters: AiAgentToolParameterDto[];
  httpRequestToolParameters: HttpRequestToolParameters;
  mcpServerId: string;
  mcpServerName: string;
  parameterSchemaJson: string;
  strict: boolean;
  chunkCount?: number;
  knowledgeBaseName?: string;
  knowledgeBaseId?: string;
}

export interface AiAgentToolCreateDto {
  name: string;
  description: string;
  type: AiAgentToolType;
  strict: boolean;
  prebuiltAiAgentToolNames?: string[];
  chunkCount?: number;
  knowledgeBaseId?: string;
}

export interface AiAgentToolUpdateDto {
  name: string;
  description: string;
  httpRequestToolParameters: HttpRequestToolParameters;
  type: AiAgentToolType;
  strict: boolean;
  chunkCount?: number;
}

export enum AiAgentToolType {
  httpRequest,
  actionFlow,
  mcp,
  knowledgeRetrieval,
}

export enum PrebuiltWorkflowType {
  endCall,
  endChat,
  transferCallToLiveAgent,
  transferChatToLiveAgent,
}

// Prebuilt workflow backend identifiers (snake_case format for API)
export const PREBUILT_WORKFLOW_BACKEND_NAMES: Record<PrebuiltWorkflowType, string> = {
  [PrebuiltWorkflowType.endCall]: 'end_call',
  [PrebuiltWorkflowType.endChat]: 'end_chat',
  [PrebuiltWorkflowType.transferCallToLiveAgent]: 'transfer_call_to_live_agent',
  [PrebuiltWorkflowType.transferChatToLiveAgent]: 'transfer_chat_to_live_agent',
};

// Prebuilt workflow display names (for UI)
export const PREBUILT_WORKFLOW_DISPLAY_NAMES: Record<PrebuiltWorkflowType, string> = {
  [PrebuiltWorkflowType.endCall]: 'End Call',
  [PrebuiltWorkflowType.endChat]: 'End Chat',
  [PrebuiltWorkflowType.transferCallToLiveAgent]: 'Transfer Call to Live Agent',
  [PrebuiltWorkflowType.transferChatToLiveAgent]: 'Transfer Chat to Live Agent',
};

export interface HttpRequestToolParameters {
  method: string;
  url: string;
  content: string;
  headers: string;
}

export interface AvailableAiAgentDto {
  id: string;
  name: string;
}

export interface AiAgentSubAgentComponentData {
  aiAgentId: string;
  subAgent: AiAgentSubAgentDto;
  availableAiAgentSubAgents: AvailableAiAgentDto[];
}

export interface AiAgentToolComponentData {
  aiAgentId: string;
  tool: AiAgentToolDto;
  projectVersionId: string;
}

export interface AiAgentKnowledgeBaseDto {
  id: string;
  name: string;
  description: string;
}

export interface AiAgentImportMcpToolsComponentData {
  aiAgentId: string;
  aiAgentTools: AiAgentToolDto[];
}

export interface ToolTypeSelection {
  selectedType: 'apiIntegration' | 'workflow' | 'knowledgeRetrieval';
  aiAgentToolType?: AiAgentToolType;
}

export interface WorkflowTypeSelection {
  workflowType: 'custom' | 'prebuilt';
  aiAgentToolType: AiAgentToolType;
  selectedWorkflows?: PrebuiltWorkflowType[];
}

export interface AiAgentToolParameterComponentData {
  aiAgentId: string;
  toolId: string;
  parameter: AiAgentToolParameterDto;
  tool?: AiAgentToolDto;
}

export enum AiAgentToolParameterType {
  string,
  number,
  boolean,
  object,
  array,
  integer,
}

export interface AiAgentToolParameterCreateDto {
  name: string | null;
  description: string;
  type: AiAgentToolParameterType;
  enumValues?: string[];
  parentParameterId?: string;
  isRequired: boolean | null;
}

export interface AiAgentToolParameterUpdateDto {
  name: string | null;
  description: string;
  type: AiAgentToolParameterType;
  enumValues?: string[];
  parentParameterId?: string;
  isRequired: boolean | null;
}

export interface AiAgentToolParameterDto extends AuditedEntityDto<string> {
  name: string | null;
  description: string;
  type: AiAgentToolParameterType;
  enumValues?: string[];
  aiAgentToolId: string;
  parentParameterId?: string;
  isRequired: boolean | null;
}

export interface AiAgentPreparationResultDto {
  aiAgentId: string;
  preparationState: PreparationState;
  lastPreparationDate?: Date;
  errorCode?: string;
}

export interface AiAgentToolFlowData {
  id: string;
  name: string;
  aiAgentId: string;
  flowId: string;
}

export interface AiAgentPreparationStatusDto {
  id: string;
  name: string;
  preparationStateMessage?: string;
}

export interface AiAgentExportInputDto {
  includeAvatar: boolean;
  includeSubAgents: boolean;
}

// -- Import Types Start --
export interface AiAgentToolParameterImportDto {
  Name: string;
  Description: string;
  Type: AiAgentToolParameterType;
  EnumValues: string[];
  IsRequired: boolean;
}

export interface AiAgentToolFlowImportDto {
  FlowType: FlowType;
  Name: string;
  Json: string;
  Parameters: string;
}

export interface AiAgentToolImportDto {
  Name: string;
  Description: string;
  Type: AiAgentToolType;
  Status: ActivationStatus;
  Flow: AiAgentToolFlowImportDto;
  AiAgentToolParameters: AiAgentToolParameterImportDto[];
  Strict: boolean;
}

export interface SubAiAgentImportDto {
  Name: string;
  Description: string;
  Instructions: string;
  CollaborationMode: CollaborationMode;
  AvoidHarmfulOrUnethicalContent: boolean;
  StayGroundedToVerifiedInformation: boolean;
  DetectAndBlockJailbreakAttempts: boolean;
  MaintainOutputConsistency: boolean;
  Persona: Persona;
  ResponseDetailLevel: ResponseDetailLevel;
  CreativityLevel: CreativityLevel;
  AiAgentTools: AiAgentToolImportDto[];
  AvatarMetadata: string | null;
  Avatar: string | null;
}

export interface AiAgentSubAgentImportDto {
  SubAgent: SubAiAgentImportDto;
  CollaborationInstructions: string;
  ShareConversationHistory: boolean;
  Status: ActivationStatus;
}

export interface AiAgentImportDto {
  Name: string;
  Description: string;
  Instructions: string;
  CollaborationMode: CollaborationMode;
  AvoidHarmfulOrUnethicalContent: boolean;
  StayGroundedToVerifiedInformation: boolean;
  DetectAndBlockJailbreakAttempts: boolean;
  MaintainOutputConsistency: boolean;
  Persona: Persona;
  ResponseDetailLevel: ResponseDetailLevel;
  CreativityLevel: CreativityLevel;
  AiAgentSubAgents: AiAgentSubAgentImportDto[];
  AiAgentTools: AiAgentToolImportDto[];
  AvatarMetadata: object | null;
  Avatar: string | null;
}
// -- Import Types End --

export interface AiAgentImportMappingInputDto {
  originalName: string;
  name: string;
  llmConfigurationId: string;
  llmModel: string;
  isCustomLlmModelSelected: boolean;
  reasoningEffort?: ReasoningEffort;
  guardrailId?: string;
}

export interface AiAgentImportInputDto {
  projectId: string;
  projectVersionId: string;
  aiAgentSummaries: AiAgentImportMappingInputDto[];
  json: string;
}

export interface AiAgentPreparationWithSubAgentsResultDto {
  totalAgentCount: number;
  results: AiAgentPreparationResultDto[];
}

export interface UserLlmConfigurationDto {
  llmConfigurationId: string;
  llmModel: string;
  isCustomLlmModelSelected: boolean;
  reasoningEffort?: ReasoningEffort;
}

export interface AiAgentMcpServerDto {
  id: string;
  name: string;
}

export interface AiAgentMcpToolDto {
  name: string;
  description: string;
  isAlreadyImported: boolean;
}

export interface McpToolNameDto {
  name: string;
}

export interface ImportMcpServerToolsDto {
  mcpServerId: string;
  toolNames: McpToolNameDto[];
}

export interface AiAgentToolImportInputDto {
  name: string;
  json: string;
}

export interface AiAgentGuardrailSummaryDto {
  id: string;
  name: string;
}

export interface UserGuardrailDto {
  guardrailId: string;
}

export interface AiAgentArchivedInstructionDetailDto {
  id: string;
  instruction: string;
  description?: string;
  creatorName: string;
  lastModificationTime: string;
}

export interface AiAgentArchivedInstructionCreateDto {
  aiAgentVersionGroupId: string;
  instruction: string;
  description?: string;
}
